'use client';

import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { CONFIG } from 'src/config-global';

import { SplashScreen } from 'src/components/loading-screen';

import { useAuthContext } from '../hooks';
import {
  getPostAuthRedirectPath,
  isEmailVerificationAllowedPath,
  isUserEmailVerified,
  isVerifyEmailPendingPath,
} from '../utils/post-auth-redirect';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthGuard({ children }: Props) {
  const router = useRouter();

  const pathname = usePathname();

  const searchParams = useSearchParams();

  const { authenticated, loading, user } = useAuthContext();

  const [isChecking, setIsChecking] = useState<boolean>(true);

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );

  const checkPermissions = async (): Promise<void> => {
    if (loading) {
      return;
    }
    if (!authenticated) {
      const { method } = CONFIG.auth;

      const signInPath = {
        jwt: paths.auth.jwt.signIn,
        auth0: paths.auth.auth0.signIn,
        amplify: paths.auth.amplify.signIn,
        firebase: paths.auth.firebase.signIn,
        supabase: paths.auth.supabase.signIn,
      }[method];

      const href = window.location.pathname === '/' 
        ? signInPath 
        : `${signInPath}?${createQueryString('returnTo', pathname)}`;
      
      router.replace(href);
      return;
    }

    if (
      authenticated &&
      user &&
      !isUserEmailVerified(user) &&
      isVerifyEmailPendingPath(pathname)
    ) {
      setIsChecking(false);
      return;
    }

    if (
      authenticated &&
      user &&
      isUserEmailVerified(user) &&
      isVerifyEmailPendingPath(pathname)
    ) {
      router.replace(getPostAuthRedirectPath(user, null));
      return;
    }

    if (
      authenticated &&
      user &&
      !isUserEmailVerified(user) &&
      !isEmailVerificationAllowedPath(pathname)
    ) {
      router.replace(paths.auth.jwt.verifyEmailPending);
      return;
    }

    if (
      authenticated &&
      user?.client?.requireMfaForAllUsers &&
      user.hasMfaEnabled !== true
    ) {
      const accountPath = paths.dashboard.account;
      const isAccountPage = pathname?.startsWith(accountPath) ?? false;
      const isAuthArea = pathname?.startsWith('/auth') ?? false;
      if (!isAccountPage && !isAuthArea) {
        router.replace(`${accountPath}#security`);
        setIsChecking(false);
        return;
      }
    }

    if (authenticated || !user?.client) {
      // const { method } = CONFIG.auth;
      // const signInPath = {
      //   jwt: paths.auth.jwt.signIn,
      //   auth0: paths.auth.auth0.signIn,
      //   amplify: paths.auth.amplify.signIn,
      //   firebase: paths.auth.firebase.signIn,
      //   supabase: paths.auth.supabase.signIn,
      // }[method];
      // @todo this need to enabled companyName
      // router.replace(href);
      // return;
    }

    if (user?.id && !user.client?.id) {
      router.push(paths.dashboard.createCompanyName.root);
    }

    setIsChecking(false);
  };

  useEffect(() => {
    checkPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, loading, user, pathname, router]);

  if (isChecking) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
